import type { OperationRun } from '@foodlista/trigger-vue'
import type { OperationItem } from '../types'
import { useQueryClient } from '@tanstack/vue-query'
import { getOperationCompletionEffect } from '../completion/registry'
import {
  addWatchedOperationId,
  removeWatchedOperationId,
} from '../utils/watched-operations'
import { useRestaurantOperations } from './useRestaurantOperations'
// Side-effect: register the operation catalog entries with the completion
// registry at module init. When new operations are added, side-effect
// import their `*.scoped.ts` module here too.
import '../catalog/allergen-detect.scoped'
import '../catalog/menu-detect-metadata.scoped'
import '../catalog/menu-translate.scoped'
import '../catalog/products-detect-metadata'
import '../catalog/text-generate.scoped'
import '../catalog/translate-products'

export interface RestaurantOperationsContext {
  restaurantSlug: ComputedRef<string | undefined>
  items: ComputedRef<OperationItem[]>
  liveByOperationId: ComputedRef<Map<string, OperationRun>>
  envelopesFetched: ComputedRef<boolean>
  realtimeAccessToken: ComputedRef<string | undefined>
  realtimeTag: ComputedRef<string | undefined>
  realtimeTokenError: ComputedRef<Error | null>
  realtimeStreamError: ComputedRef<Error | null>
  isRealtimeTokenPending: ComputedRef<boolean>
  watchedOperationIds: Ref<Set<string>>
  addWatchedOperation: (operationId: string) => void
  removeWatchedOperation: (operationId: string) => void
  invalidateEnvelopes: () => Promise<unknown>
}

const RestaurantOperationsContextKey = Symbol(
  'RestaurantOperationsContext',
) as InjectionKey<RestaurantOperationsContext>

function useOperationTerminalDispatch(items: Ref<OperationItem[]>) {
  const queryClient = useQueryClient()
  const handledEffects = new Set<string>()
  const runningEffects = new Set<string>()

  watch(
    items,
    (current) => {
      void (async () => {
        for (const item of current) {
          if (
            !item.run
            || item.run.status !== 'COMPLETED'
            || handledEffects.has(item.id)
            || runningEffects.has(item.id)
          ) {
            continue
          }

          const effect = getOperationCompletionEffect(item.type)
          if (!effect) {
            continue
          }

          runningEffects.add(item.id)
          try {
            await effect({
              operationId: item.id,
              operationType: item.type,
              restaurantSlug: item.restaurantSlug,
              contextVersion: item.contextVersion,
              context: item.context,
              run: item.run,
              queryClient,
            })
            handledEffects.add(item.id)
          } catch (error) {
            console.error('Operation completion effect failed', {
              operationId: item.id,
              operationType: item.type,
              error,
            })
          } finally {
            runningEffects.delete(item.id)
          }
        }
      })()
    },
    { deep: true },
  )
}

export function provideRestaurantOperations(
  restaurantSlug: MaybeRefOrGetter<string | undefined>,
): RestaurantOperationsContext {
  const slug = computed(() => toValue(restaurantSlug))
  const watchedOperationIds = ref(new Set<string>())

  const feed = useRestaurantOperations(slug, { watchedOperationIds })

  useOperationTerminalDispatch(feed.items)

  function addWatchedOperation(operationId: string) {
    watchedOperationIds.value = addWatchedOperationId(
      watchedOperationIds.value,
      operationId,
    )
  }

  function removeWatchedOperation(operationId: string) {
    watchedOperationIds.value = removeWatchedOperationId(
      watchedOperationIds.value,
      operationId,
    )
  }

  const context: RestaurantOperationsContext = {
    restaurantSlug: slug,
    items: feed.items,
    liveByOperationId: feed.liveByOperationId,
    envelopesFetched: feed.envelopesFetched,
    realtimeAccessToken: feed.realtimeAccessToken,
    realtimeTag: feed.realtimeTag,
    realtimeTokenError: feed.realtimeTokenError,
    realtimeStreamError: feed.realtimeStreamError,
    isRealtimeTokenPending: feed.isRealtimeTokenPending,
    watchedOperationIds,
    addWatchedOperation,
    removeWatchedOperation,
    invalidateEnvelopes: feed.invalidateEnvelopes,
  }

  provide(RestaurantOperationsContextKey, context)

  return context
}

export function useRestaurantOperationsContext(): RestaurantOperationsContext {
  const context = inject(RestaurantOperationsContextKey)
  if (!context) {
    throw new Error(
      'useRestaurantOperationsContext must be used within RestaurantOperationsProvider',
    )
  }

  return context
}
